import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import NotificationSettingsModal from './NotificationSettingsModal';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

import useApiRequest from 'Hooks/useApiRequest';
import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';

import ArrowLeftIcon from 'Assets/icons/arrow-left.svg';
import ArrowRightIcon from 'Assets/icons/arrow-right.svg';
import CogIcon from 'Assets/icons/cog.svg';

const NOTIFICATION_SETTINGS_MODAL = 'home-notification-settings-modal';
const PER_PAGE = 10;

const isRtl = () => typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

/**
 * This user's notifications in this event, unread above read.
 *
 * A ROW LIST, NOT A TABLE. A notification is a sentence and a link, not a
 * record with columns. The unread dot, the bold name and the relative time
 * carry the same information in a line the eye can scan, and none of them
 * spends width on a raw URL.
 *
 * THE ORDER IS THE SERVER'S. Unread-first is applied in the query, so it holds
 * across page boundaries; sorting a fetched page would order the wrong rows.
 *
 * Opening a notification marks it read first and then follows the link: a
 * notification you have acted on should not still be bold when you come back.
 */
export default function MyNotificationsView({ notificationsUrl, markReadUrl, markAllReadUrl, onQueueChange, myViews }) {
    const ArrowIcon = isRtl() ? ArrowLeftIcon : ArrowRightIcon;

    const settingsModal = useModal();
    const pagination = usePagination({ perPage: PER_PAGE });

    /* ONE request instance for both the list and mark-all-read, because
       mark-all-read RETURNS the first page as it now stands. Driving it through
       a second instance would land that page in a second `data` slot the list
       does not render, forcing a discard-and-refetch — two round trips and two
       loading states for one click. Overriding `url` per call keeps the answer
       where the list already reads from. */
    const list = useApiRequest({ url: notificationsUrl, displayErrorToast: false });

    /* Mark-all-read shares the list's request instance, so a failed WRITE lands
       in the same `error` the failed READ does. These two flags keep them apart:
       without them a write failure renders "we could not load your
       notifications" over rows the user was reading perfectly well, and with no
       pager on a short list there is nothing to re-run the read and clear it. */
    const [markingAll, setMarkingAll] = useState(false);
    const [markAllFailed, setMarkAllFailed] = useState(false);

    const payload = list.data?.data;
    const rows = payload?.rows ?? [];

    /* A failed write leaves `list.error` set. useApiRequest clears it at the
       start of the next request and never clears `data`, so the rows survive and
       a successful read puts everything back. */
    const listFailedToLoad = !!list.error && !markAllFailed;

    const fetchPage = () => list.get({ page: pagination.page, per_page: PER_PAGE });

    useEffect(() => {
        setMarkAllFailed(false);
        fetchPage();
    }, [pagination.page]);

    /* An Echo subscription, so a notification that arrives while the tab is
       open lands in the list.

       LEFT ON UNMOUNT, which a whole-page list would not need to do: this is a
       TAB, and leaving it unmounts the component while the page lives on. Without the
       teardown, switching away and back subscribes a second handler to the same
       channel and each notification refetches once per visit ever made. */
    useEffect(() => {
        if (!window.Echo || !myViews.event_user_id) {
            return undefined;
        }

        const channelName = `event-users.${myViews.event_user_id}`;

        window.Echo.private(channelName).notification(() => {
            // Not fetchPage(): this handler is created once per subscription and
            // closes over that render's `pagination.page`, so calling it directly
            // silently reloads whatever page was current when the tab mounted,
            // while the pager may since have moved on. Resetting AND fetching
            // page 1 explicitly — rather than leaving it to the `[pagination.page]`
            // effect alone — also covers the case where the user was already on
            // page 1: `setPage(1)` there would be a same-value no-op that never
            // fires the effect, and the new notification would not appear.
            pagination.setPage(1);
            list.get({ page: 1, per_page: PER_PAGE });
            onQueueChange?.();
        });

        // The bare name, as every other Echo teardown in the project does it —
        // Echo.leave() strips its own `private-`/`presence-` prefixes, so passing
        // the prefixed name would leave a channel nobody joined.
        return () => window.Echo.leave(channelName);
    }, [myViews.event_user_id]);

    /* The tab badge reads the entitlement payload's unread figure, so it is
       re-read alongside the list rather than decremented here — two places
       counting the same thing is how they come to differ. */
    const refreshBadge = () => onQueueChange?.();

    /* The response IS the refresh — the endpoint answers with page 1 after
       clearing, and it lands straight in the list's own `data`. The page is
       reset alongside it so a user who was deeper in the list is not left with
       page 1's rows under a pager reading 3.

       `markingAll` is separate from `list.loading` so the button spins for its
       own write and not for ordinary page navigation, and a rejection sets
       `markAllFailed` rather than being swallowed — the shared instance is
       silent (`displayErrorToast: false`), so nothing else would tell the user
       the write did not happen. */
    const markAllRead = () => {
        setMarkingAll(true);

        return list
            .request({ method: 'put', url: markAllReadUrl })
            .then(() => {
                setMarkAllFailed(false);
                pagination.setPage(1);
                refreshBadge();
            })
            .catch(() => setMarkAllFailed(true))
            .finally(() => setMarkingAll(false));
    };

    const openNotification = (event, row) => {
        if (row.read_at) {
            return;
        }

        event.preventDefault();

        /* Plain axios rather than useApiRequest: that hook's `replace` option
           rewrites the url it closed over, so the second notification marked
           read on this page would post to the first one's URL. The same call
           MyNotifications makes. */
        axios
            .put(markReadUrl.replace('NOTIFICATION_ID', row.id))
            .catch(() => {})
            .finally(() => {
                window.location.href = row.href;
            });
    };

    const unread = payload?.unread ?? 0;

    return (
        <div className="table__card">
            <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <h3 className="table__header">{$t('My notifications')}</h3>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    {unread > 0 && (
                        <Button variant="secondary" small onClick={markAllRead} loading={markingAll}>
                            {$t('Mark all read')}
                        </Button>
                    )}
                    {/* Notification settings stay where they have always been —
                        the cog beside the list. */}
                    <button type="button" className="table__icon-btn tw-text-white" aria-label={$t('Notification settings')} onClick={settingsModal.show}>
                        <CogIcon className="tw-w-3.5 tw-h-3.5" />
                    </button>
                </div>
            </div>

            {list.loading && <LoadingSpinner />}

            {listFailedToLoad && !list.loading && <Alert type="error">{$t('We could not load your notifications. Please refresh to try again.')}</Alert>}

            {/* A failed WRITE says so and leaves the list where it was. Reusing
                the load message here would tell the user their notifications
                could not be read, over the notifications they are reading. */}
            {markAllFailed && !list.loading && <Alert type="error">{$t('We could not mark your notifications read. Please try again.')}</Alert>}

            {!list.loading && !listFailedToLoad && rows.length === 0 && (
                <p className="tw-py-8 tw-text-center tw-text-sm tw-text-charcoal-60">{$t('You have no notifications in this event.')}</p>
            )}

            {!list.loading && !listFailedToLoad && rows.length > 0 && (
                <>
                    <ul className="tw-m-0 tw-list-none tw-p-0">
                        {rows.map((row) => (
                            <li
                                key={row.id}
                                className={classNames('tw-flex tw-items-start tw-gap-3 tw-border-b tw-border-charcoal-15 tw-py-3 last:tw-border-b-0', {
                                    'tw-opacity-60': !!row.read_at,
                                })}
                            >
                                <span
                                    className={classNames('tw-mt-1.5 tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full', {
                                        'tw-bg-blue': !row.read_at,
                                        'tw-bg-charcoal-25': !!row.read_at,
                                    })}
                                    /* The dot is the unread indicator, so it is
                                       named rather than left as decoration —
                                       colour alone is not an indicator. */
                                    role="img"
                                    aria-label={row.read_at ? $t('Read') : $t('Unread')}
                                />
                                <div className="tw-min-w-0 tw-flex-1">
                                    <p className={classNames('tw-mb-0 tw-text-sm tw-text-charcoal', { 'tw-font-semibold': !row.read_at })}>
                                        {row.record.data.name}
                                    </p>
                                    {!!row.record.data.description && <p className="tw-mb-0 tw-text-xs tw-text-charcoal-60">{row.record.data.description}</p>}
                                </div>
                                <span className="tw-shrink-0 tw-text-xs tw-text-charcoal-50">{moment(row.created_at).fromNow()}</span>
                                <a
                                    href={row.href}
                                    onClick={(event) => openNotification(event, row)}
                                    className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-x-1 tw-text-xs tw-font-semibold tw-text-blue tw-no-underline"
                                >
                                    {$t('Open')}
                                    <ArrowIcon className="tw-w-3 tw-h-3" />
                                </a>
                            </li>
                        ))}
                    </ul>

                    {payload.total > PER_PAGE && (
                        <div className="tw-mt-4 tw-flex tw-items-center tw-space-x-3">
                            <PaginationInfo from={payload.from} to={payload.to} total={payload.total} />
                            <TablePagination
                                lastPage={payload.last_page}
                                pageChange={(selection) => pagination.setPage(selection.selected + 1)}
                                currentPage={pagination.page}
                            />
                        </div>
                    )}
                </>
            )}

            <NotificationSettingsModal
                id={NOTIFICATION_SETTINGS_MODAL}
                isLoading={false}
                modal={settingsModal}
                fetchNotificationSettingsUrl={myViews.fetch_notification_settings_url}
                upsertNotificationSettingsUrl={myViews.upsert_notification_settings_url}
            />
        </div>
    );
}
